import Script from "next/script";
import { useContext, useEffect, useState } from "react";
import { googleCheckUrl, googleUrl } from "@/constants/paths";
import Loading from "../Loading/Loading";
import OrgNameDialog from "./OrgNameDialog";
import { userExistsError } from "./functions";
import UserExistsErrorDialog from "./UserExistsErrorDialog";
import { css } from "@emotion/react";
import { darkRedColor } from "@/constants/colors";
import { BodyRegular14, H7Css, Heading } from "../Typography";
import { captureException } from "@sentry/nextjs";
import { ScheduleGeneratorContext } from "./ScheduleGeneratorContext";
import posthog from "posthog-js";
import { useCookies } from "react-cookie";
import { gotham } from "@/utils/fonts";

const GoogleSignUpButton = () => {
  const [cookies] = useCookies(["jwwa_track"]);
  const jwwaTrackCookie = cookies["jwwa_track"];
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const token = useContext(ScheduleGeneratorContext);
  const [idToken, setIdToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element>("");
  const [orgNameError, setOrgNameError] = useState("");
  const [showUserExistsErrorDialog, setShowUserExistsErrorDialog] =
    useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!scriptLoaded) return;

    // @ts-ignore
    gapi.load("auth2", function () {
      // @ts-ignore
      // Retrieve the singleton for the GoogleAuth library and set up the client.
      const auth2 = gapi.auth2.init({
        client_id: process.env.googleClientId,
        cookiepolicy: "single_host_origin",
      });

      const button = document.getElementById("google-signup-button");
      if (button) {
        button.addEventListener("click", () => {
          auth2.signIn({ prompt: "select_account" }).then((googleUser: any) => {
            handleCredentialResponse(googleUser.getAuthResponse().id_token);
          });
        });
      }
    });
  }, [scriptLoaded]);

  const handleCredentialResponse = async (idToken: string) => {
    try {
      if (loading) return;
      setLoading(true);
      posthog.capture("JWMarketing - Sign Up Check - Started", {
        login_type: "google",
      });
      const res = await fetch(googleCheckUrl, {
        method: "POST",
        body: JSON.stringify({
          token_id: idToken,
          jwwaTrackCookie,
        }),
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 200) {
        setIdToken(idToken);
        posthog.capture(
          "JWMarketing - Sign Up Check - Org Name Dialog Opened",
          {
            login_type: "google",
          }
        );
      } else {
        const { message } = await res.json();

        if (userExistsError(message)) {
          setShowUserExistsErrorDialog(true);
          posthog.capture("JWMarketing - Sign Up Check Failed - User Exists", {
            login_type: "google",
          });
        } else {
          setErrorMessage(message);
          posthog.capture("JWMarketing - Sign Up Check Failed - Other Error", {
            login_type: "google",
          });
        }
      }
    } catch (e) {
      captureException(e);
      posthog.capture("JWMarketing - Sign Up Check Failed - Server Error", {
        login_type: "google",
      });
    }
    setLoading(false);
  };

  const handleSubmit = (orgName: string) => async (e: any) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const response = await fetch(googleUrl, {
        method: "POST",
        body: JSON.stringify({
          token_id: idToken,
          org_name: orgName,
          jwwaTrackCookie,
          ph_distinct_id: posthog.get_distinct_id(),
          ph_session_id: posthog.get_session_id(),
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Schedule-Generator-Token": token || "",
        },
        credentials: "include",
      });
      const json = await response.json();
      if (json.redirect) {
        window.location.href = json.redirect;
        posthog.capture("JWMarketing - Sign Up - Success Redirect", {
          login_type: "google",
        });
      } else {
        const orgNameError = json.errors?.["organization.name"]?.[0];
        let errorMessage;
        if (orgNameError) {
          errorMessage = orgNameError;
          posthog.capture("JWMarketing - Sign Up - Org Name Error", {
            login_type: "google",
          });
        } else {
          // TODO: sentry capture exception

          errorMessage =
            "Hmm something went wrong. Please refresh the page and try again.";
          posthog.capture("JWMarketing - Sign Up - Other Error", {
            login_type: "google",
          });
        }
        setOrgNameError(errorMessage);
        setLoading(false);
      }
    } catch (e) {
      const errorMessage =
        "Hmm something went wrong. Please refresh the page and try again.";
      setOrgNameError(errorMessage);
      setLoading(false);
      captureException(e);
      posthog.capture("JWMarketing - Sign Up - Server Error", {
        login_type: "google",
      });
    }
  };

  return (
    <>
      <Loading open={loading} />
      <UserExistsErrorDialog
        isOpen={showUserExistsErrorDialog}
        onRequestClose={() => setShowUserExistsErrorDialog(false)}
      />
      <Script
        src="https://apis.google.com/js/api:client.js"
        strategy="afterInteractive"
        onReady={() => setScriptLoaded(true)}
      />
      {errorMessage && (
        <div
          css={css`
            background-color: ${darkRedColor};
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
            color: #fff;
          `}
        >
          <Heading
            css={css`
              ${H7Css}
              margin-bottom: 14px;
            `}
          >
            Oops!
          </Heading>
          <BodyRegular14>{errorMessage}</BodyRegular14>
        </div>
      )}
      <button
        id="google-signup-button"
        css={css`
          width: 100%;
          height: 50px;
          border-radius: 10px;
          border: 2px solid #daf2ff;
          background-color: transparent;
          color: #243b4a;
          font-size: 16px;
          font-weight: 350;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          ${gotham.style};
          &:hover {
            background-color: #daf2ff;
          }
        `}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21.7861 8.84565H20.9V8.8H11V13.2H17.2167C16.3097 15.7614 13.8727 17.6 11 17.6C7.35515 17.6 4.4 14.6448 4.4 11C4.4 7.35515 7.35515 4.4 11 4.4C12.6825 4.4 14.2131 5.0347 15.3786 6.07145L18.4899 2.9601C16.5253 1.12915 13.8974 0 11 0C4.92525 0 0 4.92525 0 11C0 17.0748 4.92525 22 11 22C17.0748 22 22 17.0748 22 11C22 10.2625 21.9241 9.5425 21.7861 8.84565Z"
            fill="#FFC107"
          />
          <path
            d="M1.2666 5.88005L4.88065 8.5305C5.85855 6.1094 8.22685 4.4 10.9983 4.4C12.6808 4.4 14.2114 5.0347 15.3769 6.07145L18.4882 2.9601C16.5236 1.12915 13.8957 0 10.9983 0C6.7732 0 3.1091 2.38535 1.2666 5.88005Z"
            fill="#FF3D00"
          />
          <path
            d="M11.0008 21.9998C13.8421 21.9998 16.4238 20.9124 18.3757 19.1442L14.9712 16.2633C13.8297 17.1314 12.4349 17.6009 11.0008 17.5998C8.13966 17.5998 5.71031 15.7754 4.79511 13.2295L1.20801 15.9932C3.02851 19.5556 6.72561 21.9998 11.0008 21.9998Z"
            fill="#4CAF50"
          />
          <path
            d="M21.7861 8.84594H20.9V8.80029H11V13.2003H17.2167C16.7828 14.4193 16.0013 15.4845 14.9688 16.2643L14.9705 16.2632L18.375 19.1441C18.1341 19.363 22 16.5003 22 11.0003C22 10.2627 21.9241 9.54279 21.7861 8.84594Z"
            fill="#1976D2"
          />
        </svg>
        Sign Up With Google
      </button>
      <OrgNameDialog
        loading={loading}
        open={Boolean(idToken)}
        onRequestClose={() => setIdToken("")}
        onSubmit={handleSubmit}
        errorMessage={orgNameError}
      />
    </>
  );
};

export default GoogleSignUpButton;
